import { useAuth } from "@/_core/hooks/useAuth";
import { BRAND } from "@/brand";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { LogOut, Menu } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";

type NavItem = { href: string; label: string; icon?: ReactNode };

interface PrivateLayoutProps {
  children: ReactNode;
  nav: NavItem[];
  title: string;
  requireRole?: "user" | "admin";
}

export default function PrivateLayout({ children, nav, title, requireRole }: PrivateLayoutProps) {
  const { user, loading, isAuthenticated } = useAuth();
  const [location] = useLocation();
  const logout = trpc.auth.logout.useMutation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      window.location.href = getLoginUrl();
    }
  }, [loading, isAuthenticated]);

  useEffect(() => {
    if (requireRole === "admin" && user && user.role !== "admin") {
      window.location.href = "/mi-cuenta";
    }
  }, [requireRole, user]);

  const handleLogout = async () => {
    await logout.mutateAsync();
    window.location.href = "/";
  };

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--cream)]">
        <div className="animate-pulse text-[var(--forest)]">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--cream)] flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-72 bg-[var(--forest-deep)] text-[var(--cream)] flex-col z-40 transition-transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } flex`}
      >
        <Link href="/" className="flex items-center h-20 px-6 border-b border-[var(--cream)]/10 shrink-0">
          <img src={BRAND.logoCreamHorizontal} alt="Golf Motion Lab" className="h-9 object-contain" />
        </Link>

        <div className="px-6 py-5 border-b border-[var(--cream)]/10">
          <div className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[var(--bronze-light)] mb-1">
            {title}
          </div>
          <div className="text-sm font-medium text-[var(--cream)] truncate">{user?.name || user?.email}</div>
          {user?.role === "admin" && (
            <div className="mt-1.5 inline-flex px-2 py-0.5 rounded-full bg-[var(--bronze)]/25 text-[10px] font-semibold tracking-wide uppercase text-[var(--bronze-light)]">
              Administrador
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {nav.map((item) => {
            const active = location === item.href || (item.href !== "/" && location.startsWith(item.href));
            return (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? "bg-[var(--cream)]/10 text-[var(--cream)]"
                    : "text-[var(--cream)]/65 hover:bg-[var(--cream)]/5 hover:text-[var(--cream)]"
                }`}
              >
                {item.icon}
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[var(--cream)]/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-[var(--cream)]/65 hover:bg-[var(--cream)]/5 hover:text-[var(--cream)] transition-colors"
          >
            <LogOut size={16} />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <main className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-20 bg-[var(--cream)] border-b border-border h-14 flex items-center px-4 gap-3">
          <button onClick={() => setMobileOpen(true)} className="p-2">
            <Menu size={22} />
          </button>
          <img src={BRAND.logoGreenHorizontal} alt="Golf Motion Lab" className="h-7" />
        </div>
        {children}
      </main>
    </div>
  );
}
